/**
 * Category links: a shop's website opens on the category in its address.
 *
 * KEZA'S EMPIRE UG (2 Nov 2026) filters her website to Dresses, copies the
 * address and puts it in her WhatsApp catalogue. A customer who taps it has to
 * land on Dresses, on any phone. Instead they got the whole shop. The filter
 * wrote the chips' DISPLAY names into the address
 * (?category=Women%27s+Clothing&subcategory=Dresses), but the page reads the
 * address as SLUGS, so on a fresh phone nothing matched and every product
 * showed. On the phone that made the link it looked fine, because that phone
 * had already filtered in the browser.
 *
 * The address now carries the shop's own readable slugs. The filter writes them
 * (app/[domain]/ShopClient.tsx) and the page reads them back on first load
 * (app/[domain]/page.tsx) with these helpers:
 *
 *   https://kezasempire.kuttosfashion.com/?category=women-s-clothing&subcategory=dresses
 *
 * A value can be a slug, an id or a display name, because links already shared
 * carry names. It is matched against the SHOP's own taxonomy (its filter
 * options), never the platform-wide tables, where a few slugs are shared
 * between categories. A value that matches nothing is left exactly as it came,
 * which is what the page did with it before.
 *
 * Pure: no React, no Convex.
 */

/** One category, subcategory or product type as the shop's filter options carry it. */
export interface TaxonomyOption {
  _id?: string;
  name: string;
  slug?: string;
  /** A subcategory's category. */
  categoryId?: string;
  /** A product type's subcategory. */
  subcategoryId?: string;
}

/** The shop's taxonomy: getShopFilterOptions' three lists (extra fields ignored). */
export interface TaxonomyOptions {
  categories?: readonly TaxonomyOption[] | null;
  subcategories?: readonly TaxonomyOption[] | null;
  productTypes?: readonly TaxonomyOption[] | null;
}

/** The address keys of a category link, in the order a link is written. */
export const CATEGORY_LINK_KEYS = ["category", "subcategory", "productType"] as const;

export type CategoryLinkValues = {
  category?: string | null;
  subcategory?: string | null;
  productType?: string | null;
};

export interface ResolvedCategoryLink {
  category?: TaxonomyOption;
  subcategory?: TaxonomyOption;
  productType?: TaxonomyOption;
}

/** A search param's value. It can arrive as an array (?category=a&category=b), so take the first. */
export function firstParam(value: unknown): string | undefined {
  const v = Array.isArray(value) ? value[0] : value;
  if (typeof v !== "string") return undefined;
  const trimmed = v.trim();
  return trimmed ? trimmed : undefined;
}

/** "Women's Clothing", "women-s-clothing" and "WOMENS CLOTHING" all give "womensclothing". */
export function looseKey(value: string): string {
  return value
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/**
 * The option an address value names. Tries, in order: the slug, the id, the
 * display name (ignoring case), then a loose match that ignores spaces and
 * punctuation (a name or slug typed or mangled by hand). When a tier has several
 * hits, `prefer` picks one, for example a subcategory inside the chosen category.
 */
export function findTaxonomyOption<T extends TaxonomyOption>(
  value: unknown,
  options: readonly T[] | null | undefined,
  prefer?: (option: T) => boolean,
): T | undefined {
  const v = firstParam(value);
  if (!v || !options || options.length === 0) return undefined;
  const lower = v.toLowerCase();
  const loose = looseKey(v);
  const tiers: Array<(o: T) => boolean> = [
    (o) => typeof o.slug === "string" && o.slug.toLowerCase() === lower,
    (o) => typeof o._id === "string" && o._id === v,
    (o) => typeof o.name === "string" && o.name.trim().toLowerCase() === lower,
    (o) =>
      loose.length > 0 &&
      ((typeof o.slug === "string" && looseKey(o.slug) === loose) ||
        (typeof o.name === "string" && looseKey(o.name) === loose)),
  ];
  for (const tier of tiers) {
    const hits = options.filter(tier);
    if (hits.length === 0) continue;
    return (prefer ? hits.find(prefer) : undefined) ?? hits[0];
  }
  return undefined;
}

/**
 * Category, subcategory and product type of a link, against the shop's
 * taxonomy. A link that names only a deeper level still opens with its parents
 * chosen (?subcategory=dresses opens on Women's Clothing > Dresses). The filter
 * only offers a subcategory once a category is picked, so the view looks the
 * same as when someone taps their way there.
 */
export function resolveCategoryLink(
  values: CategoryLinkValues,
  options: TaxonomyOptions | null | undefined,
): ResolvedCategoryLink {
  const categories = options?.categories ?? [];
  const subcategories = options?.subcategories ?? [];
  const productTypes = options?.productTypes ?? [];

  let category = findTaxonomyOption(values.category, categories);
  let subcategory = findTaxonomyOption(
    values.subcategory,
    subcategories,
    category?._id ? (s) => s.categoryId === category!._id : undefined,
  );
  const productType = findTaxonomyOption(
    values.productType,
    productTypes,
    subcategory?._id ? (p) => p.subcategoryId === subcategory!._id : undefined,
  );

  if (!subcategory && productType?.subcategoryId) {
    subcategory = subcategories.find((s) => s._id === productType.subcategoryId);
  }
  if (!category && subcategory?.categoryId) {
    category = categories.find((c) => c._id === subcategory!.categoryId);
  }
  return { category, subcategory, productType };
}

/**
 * The chip name to show for an address value: the matched option's name, or
 * the value itself when nothing matched. That is the page's old behaviour, and
 * it is already a name when the filter wrote it.
 */
export function chipNameFor(
  option: TaxonomyOption | undefined,
  raw: string | undefined,
): string | undefined {
  return option?.name ?? raw;
}

/** The value the page sends to the shop query: the matched slug, or the value as it came. */
export function querySlugFor(
  option: TaxonomyOption | undefined,
  raw: string | undefined,
): string | undefined {
  return option?.slug ?? raw;
}

/**
 * The value the filter writes into the address for a chosen chip: its slug from
 * the first name-to-slug map that knows it. If no map knows it, the name itself
 * is written, which the page still matches by name.
 */
export function linkValueFor(
  name: string | undefined,
  ...nameToSlug: ReadonlyArray<Readonly<Record<string, string>> | null | undefined>
): string | undefined {
  if (!name || name === "All") return name;
  for (const map of nameToSlug) {
    const slug = map?.[name];
    if (typeof slug === "string" && slug) return slug;
  }
  return name;
}

/** name-to-slug pairs as options, so a cascade list can be searched like the filter options. */
export function optionsFromSlugMap(
  map: Readonly<Record<string, string>> | null | undefined,
): TaxonomyOption[] {
  if (!map) return [];
  return Object.entries(map).map(([name, slug]) => ({ name, slug }));
}

/**
 * Names of the children inside a resolved parent: the subcategories of a
 * category, or the product types of a subcategory. They are the lists the
 * filter shows after someone taps that parent. Returns [] when there is no
 * parent, and the caller keeps its full list.
 */
export function childNamesOf(
  parent: TaxonomyOption | undefined,
  children: readonly TaxonomyOption[] | null | undefined,
  parentKey: "categoryId" | "subcategoryId",
): string[] {
  if (!parent?._id || !children) return [];
  const names: string[] = [];
  for (const child of children) {
    if (child[parentKey] === parent._id && typeof child.name === "string" && !names.includes(child.name)) {
      names.push(child.name);
    }
  }
  return names;
}

/**
 * The query string of a category link, used for its share-card address
 * (og:url). It holds only the link's own keys plus the branch, never tracking
 * tags such as fbclid or utm_*. Returns "" when the address names no category,
 * and the page then keeps its home share card.
 */
export function categoryLinkQuery(searchParams: Record<string, unknown> | null | undefined): string {
  if (!searchParams) return "";
  const out = new URLSearchParams();
  for (const key of CATEGORY_LINK_KEYS) {
    const value = firstParam(searchParams[key]);
    if (value && value !== "All") out.set(key, value);
  }
  if (out.toString() === "") return "";
  const store = firstParam(searchParams.store);
  if (store && store !== "All") out.set("store", store);
  return out.toString();
}

/**
 * Where the product shop lives on the brand's public address. Normally "/". A
 * coded site (customSiteKey), or legacy AI pages on a live custom domain,
 * moves it to "/shop". This mirrors proxy.ts CASE 3 and CASE 4.5.
 */
export function shopPathOf(brand: {
  customSiteKey?: string | null;
  hasCustomPages?: boolean | null;
  liveCustomDomain?: boolean;
}): "/" | "/shop" {
  if (brand.customSiteKey) return "/shop";
  if (brand.hasCustomPages && brand.liveCustomDomain) return "/shop";
  return "/";
}
